import cn from 'classnames';
import { useState } from 'react';
import { Form } from 'react-bootstrap';
import { MdClose } from 'react-icons/md';
import { Button } from '@Components/Button';
import { IconButton } from '@Components/IconButton';
import { Appearance, Design, Size } from '@Components/designConstants';
import { AuditReport, ClientType, ClientTypeType, isClientType } from '../digital-audit-constants';
import {
  EditableMilestone,
  JourneyLibraryEntry,
  JourneyMilestoneResult,
  JourneyModel,
  JourneyStageResult,
  MilestoneStatus,
  MilestoneStatusType
} from '../engine/journey-mapper-constants';
import { journeyStageAnchor } from '../comments-constants';
import { CommentThread } from './CommentThread';
import { FoundationGauge } from './FoundationGauge';
import { ObservationCard } from './ObservationCard';

type RemapFn = (
  clientType: ClientTypeType,
  vertical: string,
  milestones?: EditableMilestone[]
) => void;

interface JourneyViewProps {
  report: AuditReport | null;
  onRemap?: RemapFn;
}

const MILESTONE_MARK: Record<MilestoneStatusType, string> = {
  [MilestoneStatus.COVERED]: '✓',
  [MilestoneStatus.GAP]: '△',
  [MilestoneStatus.DISCOVERY]: '○'
};

const MAX_TOOLS_SHOWN = 4;

const toEditable = (m: JourneyMilestoneResult): EditableMilestone => ({
  stage: m.stage,
  name: m.name,
  signal: m.signal,
  purpose: m.purpose,
  notes: m.notes
});

/** Flatten the journey's milestones, substituting a draft for one stage. */
const flattenWithDraft = (
  journey: JourneyModel,
  draftStage: number | null,
  draft: EditableMilestone[]
): EditableMilestone[] =>
  journey.stages.flatMap(s => (s.num === draftStage ? draft : s.milestones.map(toEditable)));

interface MilestoneRowProps {
  milestone: JourneyMilestoneResult;
  capabilities: Record<string, string>;
}

const MilestoneRow = ({ milestone, capabilities }: MilestoneRowProps) => {
  let platform: string;
  let platformClass = '';
  if (milestone.status === MilestoneStatus.COVERED) {
    const shown = milestone.tools.slice(0, MAX_TOOLS_SHOWN).join(' · ');
    const extra = milestone.tools.length - MAX_TOOLS_SHOWN;
    platform = extra > 0 ? `${shown} +${extra}` : shown;
  } else if (milestone.status === MilestoneStatus.GAP) {
    const needs = milestone.requires.map(cap => capabilities[cap] ?? cap).join(' or ');
    platform = `gap — needs ${needs}`;
    platformClass = 'gap';
  } else {
    platform = 'confirm in discovery';
    platformClass = 'discovery';
  }

  return (
    <div className={cn('digital-audit-ms', milestone.status)}>
      <span className='digital-audit-ms-mk'>{MILESTONE_MARK[milestone.status]}</span>
      <span className='digital-audit-ms-body'>
        <span className='digital-audit-ms-name'>{milestone.name}</span>
        {milestone.signal && <span className='digital-audit-ms-sig'>{milestone.signal}</span>}
        <span className={cn('digital-audit-ms-plat', platformClass)}>{platform}</span>
      </span>
    </div>
  );
};

interface StageCardProps {
  stage: JourneyStageResult;
  capabilities: Record<string, string>;
  editing: boolean;
  onEdit?: () => void;
}

const stageCoverageClass = (stage: JourneyStageResult): string => {
  if (!stage.detectable) return 'na';
  if (stage.covered === stage.detectable) return 'full';
  return stage.covered ? 'part' : 'none';
};

const StageCard = ({ stage, capabilities, editing, onEdit }: StageCardProps) => (
  <div className={cn('digital-audit-stage', { editing })}>
    <div className='digital-audit-stage-head'>
      <span className='digital-audit-stage-name'>
        {stage.num}. {stage.name}
      </span>
      <span className={cn('digital-audit-cov', stageCoverageClass(stage))}>
        {stage.detectable ? `${stage.covered}/${stage.detectable}` : '—'}
      </span>
    </div>
    {stage.milestones.map(m => (
      <MilestoneRow key={m.no} milestone={m} capabilities={capabilities} />
    ))}
    <CommentThread anchor={journeyStageAnchor(stage.num)} />
    {onEdit && (
      <div className='digital-audit-stage-actions'>
        <Button
          appearance={Appearance.OUTLINED}
          design={Design.NEUTRAL}
          size={Size.SM}
          onClick={onEdit}
        >
          edit milestones
        </Button>
      </div>
    )}
  </div>
);

interface StageEditorProps {
  stage: JourneyStageResult;
  draft: EditableMilestone[];
  library: JourneyLibraryEntry[];
  onDraftChange: (draft: EditableMilestone[]) => void;
  onApply: () => void;
  onCancel: () => void;
}

const StageEditor = ({
  stage,
  draft,
  library,
  onDraftChange,
  onApply,
  onCancel
}: StageEditorProps) => {
  const [librarySelection, setLibrarySelection] = useState('');

  const sections = library.reduce<Record<string, { index: number; entry: JourneyLibraryEntry }[]>>(
    (acc, entry, index) => {
      (acc[entry.section] = acc[entry.section] ?? []).push({ index, entry });
      return acc;
    },
    {}
  );

  const addFromLibrary = () => {
    const entry = library[Number(librarySelection)];
    if (!entry) return;
    onDraftChange([
      ...draft,
      { stage: stage.num, name: entry.name, signal: entry.signal, purpose: entry.purpose }
    ]);
    setLibrarySelection('');
  };

  return (
    <div className='digital-audit-jedit'>
      <span className='digital-audit-jedit-label'>
        {stage.num}. {stage.name} — milestones
      </span>
      {draft.map((m, k) => (
        // Draft rows have no stable identity while being edited — index keys
        // are correct here because rows are only appended or removed.

        <div key={k} className='digital-audit-mrow'>
          <Form.Control
            type='text'
            maxLength={120}
            value={m.name}
            onChange={e =>
              onDraftChange(
                draft.map((row, i) => (i === k ? { ...row, name: e.target.value } : row))
              )
            }
          />
          <IconButton
            design={Design.NEUTRAL}
            size={Size.SM}
            aria-label='Remove milestone'
            onClick={() => onDraftChange(draft.filter((_, i) => i !== k))}
          >
            <MdClose />
          </IconButton>
        </div>
      ))}
      <div className='digital-audit-jedit-row'>
        <Form.Select
          className='digital-audit-libsel'
          aria-label='Add from milestone library'
          value={librarySelection}
          onChange={e => setLibrarySelection(e.target.value)}
        >
          <option value=''>Add from milestone library…</option>
          {Object.entries(sections).map(([section, items]) => (
            <optgroup key={section} label={section}>
              {items.map(({ index, entry }) => (
                <option key={`${entry.name}-${index}`} value={index}>
                  {entry.name}
                </option>
              ))}
            </optgroup>
          ))}
        </Form.Select>
        <Button
          appearance={Appearance.OUTLINED}
          design={Design.NEUTRAL}
          size={Size.SM}
          disabled={librarySelection === ''}
          onClick={addFromLibrary}
        >
          add
        </Button>
        <Button
          appearance={Appearance.OUTLINED}
          design={Design.NEUTRAL}
          size={Size.SM}
          onClick={() => onDraftChange([...draft, { stage: stage.num, name: '', signal: '' }])}
        >
          + custom milestone
        </Button>
      </div>
      <div className='digital-audit-jedit-row'>
        <Button size={Size.SM} onClick={onApply}>
          Apply
        </Button>
        <Button
          appearance={Appearance.OUTLINED}
          design={Design.NEUTRAL}
          size={Size.SM}
          onClick={onCancel}
        >
          cancel
        </Button>
      </div>
    </div>
  );
};

// Continuous heat scale, ported from the reference tool's heatMap():
// hue 8 (red) -> 145 (green); the discovery-only case renders as neutral.
const heatBackground = (frac: number): string =>
  `hsl(${Math.round(8 + frac * 137)}, 60%, ${Math.round(37 + frac * 7)}%)`;

interface HeatColumnProps {
  stage: JourneyStageResult;
}

const HeatColumn = ({ stage }: HeatColumnProps) => {
  const frac = stage.detectable ? stage.covered / stage.detectable : null;

  return (
    <div className='digital-audit-heat-col'>
      <div
        className={cn('digital-audit-heat-cell', { na: frac === null })}
        style={frac === null ? undefined : { background: heatBackground(frac) }}
        title={`${stage.name}: ${stage.covered} of ${stage.detectable} detectable milestone signals supported by the stack`}
      >
        {frac === null ? 'n/a' : `${Math.round(frac * 100)}%`}
        <div className='digital-audit-heat-caption'>
          {frac === null ? 'discovery only' : `${stage.covered}/${stage.detectable} signals`}
        </div>
      </div>
      <div className='digital-audit-heat-caption'>
        {stage.num}. {stage.name}
      </div>
    </div>
  );
};

interface FoundationGroupCardProps {
  label: string;
  groupKey: string;
  journey: JourneyModel;
}

const FoundationGroupCard = ({ label, groupKey, journey }: FoundationGroupCardProps) => {
  const items = journey.foundation.filter(f => (f.group || 'site') === groupKey);
  if (items.length === 0) return undefined;
  const present = items.filter(f => f.present).length;

  return (
    <div className='digital-audit-foundation-card'>
      <div className='digital-audit-foundation-body'>
        <div className='digital-audit-foundation-title'>
          {label} {present}/{items.length}
        </div>
        {items.map(f => (
          <div
            key={f.cap}
            className={cn('digital-audit-foundation-item', f.present ? 'present' : 'absent')}
          >
            <span className='digital-audit-foundation-mk'>{f.present ? '✓' : '—'}</span>
            <span>
              {f.label}
              {f.tools.length > 0 && ` · ${f.tools.join(', ')}`}
            </span>
          </div>
        ))}
      </div>
      <div className='digital-audit-gauge'>
        <FoundationGauge present={present} total={items.length} />
        <div className='digital-audit-heat-caption'>{groupKey} coverage</div>
      </div>
    </div>
  );
};

export const JourneyView = ({ report, onRemap }: JourneyViewProps) => {
  const [editingStage, setEditingStage] = useState<number | null>(null);
  const [draft, setDraft] = useState<EditableMilestone[]>([]);

  if (!report) {
    return (
      <div className='digital-audit-page'>
        <div className='digital-audit-empty-state'>Run a scan to see journey alignment.</div>
      </div>
    );
  }

  const { journey } = report;
  const discoveryTotal = journey.stages.reduce((acc, s) => acc + s.discovery, 0);
  const clientType = isClientType(journey.clientType) ? journey.clientType : ClientType.ECOMM;
  const editedStage = journey.stages.find(s => s.num === editingStage);

  const closeEditor = () => {
    setEditingStage(null);
    setDraft([]);
  };

  const applyEdit = () => {
    onRemap?.(clientType, journey.vertical, flattenWithDraft(journey, editingStage, draft));
    closeEditor();
  };

  const addSuggestion = (index: number) => {
    const s = journey.suggestions[index];
    if (!s) return;
    closeEditor();
    onRemap?.(clientType, journey.vertical, [
      ...flattenWithDraft(journey, null, []),
      { stage: s.stage, name: s.name, signal: s.signal, purpose: s.purpose }
    ]);
  };

  return (
    <div className='digital-audit-page'>
      <div className='digital-audit-section'>
        <div className='digital-audit-section-head'>
          <div className='digital-audit-section-title'>User journey alignment</div>
          <div className='digital-audit-section-count'>
            {journey.clientName || journey.domain} · {journey.score.covered}/
            {journey.score.detectable} detectable milestones supported ({journey.score.pct}%)
          </div>
        </div>
        {onRemap && (
          <div className='digital-audit-jctx'>
            <Button
              appearance={Appearance.OUTLINED}
              design={Design.NEUTRAL}
              size={Size.SM}
              onClick={() => {
                closeEditor();
                onRemap(clientType, journey.vertical, undefined);
              }}
            >
              reset to template
            </Button>
          </div>
        )}
        <div className='digital-audit-journey-ctx'>
          {journey.clientTypeLabel} · {journey.verticalLabel} · template: {journey.templateUsed} — ✓
          supported · △ gap · ○ confirm in discovery
          {onRemap && ' — milestones are editable; exports reflect your edits'}
        </div>
        <div className='digital-audit-rail'>
          {journey.stages.map(stage => (
            <StageCard
              key={stage.num}
              stage={stage}
              capabilities={journey.capabilities}
              editing={stage.num === editingStage}
              onEdit={
                onRemap &&
                (() => {
                  setEditingStage(stage.num);
                  setDraft(stage.milestones.map(toEditable));
                })
              }
            />
          ))}
        </div>

        {editedStage && (
          <StageEditor
            stage={editedStage}
            draft={draft}
            library={journey.library}
            onDraftChange={setDraft}
            onApply={applyEdit}
            onCancel={closeEditor}
          />
        )}

        <div>
          <div className='digital-audit-heat-label'>
            Stage coverage · % of detectable milestone signals supported
          </div>
          <div className='digital-audit-heat-row'>
            {journey.stages.map(stage => (
              <HeatColumn key={stage.num} stage={stage} />
            ))}
          </div>
        </div>

        <div className='digital-audit-foundation-grid'>
          {Object.entries(journey.foundationGroups).map(([groupKey, label]) => (
            <FoundationGroupCard
              key={groupKey}
              label={label}
              groupKey={groupKey}
              journey={journey}
            />
          ))}
        </div>

        {discoveryTotal > 0 && (
          <div className='digital-audit-subtitle'>
            {discoveryTotal} milestone(s) not observable client-side — confirm in discovery.
          </div>
        )}
      </div>

      {(journey.recommendations.length > 0 || journey.suggestions.length > 0) && (
        <div className='digital-audit-section'>
          <div className='digital-audit-section-head'>
            <div className='digital-audit-section-title'>Journey recommendations</div>
            <div className='digital-audit-section-count'>
              {journey.recommendations.length + journey.suggestions.length} items — expand for
              detail
            </div>
          </div>
          {journey.recommendations.length > 0 && (
            <div>
              <span className='digital-audit-obs-group-label'>
                Prioritized gaps &amp; risks · revenue-nearest first
              </span>
              <div className='digital-audit-obs-list'>
                {journey.recommendations.map(r => (
                  <ObservationCard
                    key={r.title}
                    severity={r.severity}
                    title={r.title}
                    note={r.note}
                    more={r.more}
                  />
                ))}
              </div>
            </div>
          )}
          {journey.suggestions.length > 0 && (
            <div>
              <span className='digital-audit-obs-group-label'>
                Suggested non-standard milestones · audit evidence beyond the standard template
              </span>
              <div className='digital-audit-obs-list'>
                {journey.suggestions.map((s, index) => (
                  <ObservationCard
                    key={s.name}
                    severity={`S${s.stage}`}
                    title={`${s.name} — ${s.section}`}
                    note={`${s.reason}. ${s.purpose} · Primary signal: ${s.signal} · ${s.stageName}`}
                    more={s.more}
                  >
                    {onRemap && (
                      <Button
                        appearance={Appearance.OUTLINED}
                        size={Size.SM}
                        onClick={e => {
                          e.stopPropagation();
                          addSuggestion(index);
                        }}
                      >
                        + add to stage {s.stage}
                      </Button>
                    )}
                  </ObservationCard>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
